import { clearEnvAuthCookies } from '@tt/cookie';

export const FORCED_LOGOUT_STORAGE_KEY = 'forceLogout';
export const FORCED_LOGOUT_EVENT = 'app:forced-logout';
export const FORCED_LOGOUT_NOTICE_KEY = 'forcedLogoutNotice';

const FORCED_LOGOUT_CODE = 'FORCED_LOGOUT';
const TOKEN_UPGRADED_CODE = 'TOKEN_UPGRADED';
const FORCED_LOGOUT_LOGIN_URL = 'https://tradingterminal.com/auth/login';
const TT_PROD_HOST = 'tradingterminal.com';
const TT_STG_HOST = 'tt-stg.tradingterminal.com';
const TT_DEV_HOST = 'tt-dev.tradingterminal.com';
const TT_LOGIN_PATH = '/auth/login';
const CANONICAL_TT_HOSTS = new Set([TT_PROD_HOST, TT_STG_HOST, TT_DEV_HOST]);
let isForcedLogoutInProgress = false;
const FORCED_LOGOUT_QUERY_PARAM = 'forcedLogout';

const getCodeFromData = (data) => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const nestedError =
        'error' in data && typeof data.error === 'object' && data.error ? data.error : null;
    const code =
        ('code' in data ? data.code : null) ||
        ('errorCode' in data ? data.errorCode : null) ||
        (nestedError && 'code' in nestedError ? nestedError.code : null);

    return typeof code === 'string' ? code : null;
};

const getErrorCode = (error) => {
    return getCodeFromData(error?.response?.data);
};

export const isForcedLogoutError = (error) => {
    if (!error || typeof error !== 'object') {
        return false;
    }

    return (
        error?.response?.status === 401 &&
        [FORCED_LOGOUT_CODE, 'SESSION_REVOKED'].includes(getErrorCode(error))
    );
};

export const isSessionRevokedError = (error) =>
    error?.response?.status === 401 && getErrorCode(error) === 'SESSION_REVOKED';

export const clearAuthStorage = ({ onCleanup } = {}) => {
    if (typeof onCleanup === 'function') {
        try {
            onCleanup();
        } catch {
            // local cleanup should never block forced-logout flow
        }
    }

    if (typeof window === 'undefined') {
        return;
    }

    clearEnvAuthCookies();

    try {
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('user');
        window.localStorage.removeItem('staging-user');
        window.localStorage.removeItem('stg-user');
        window.localStorage.removeItem('dev-user');
        window.localStorage.removeItem('next');
        window.localStorage.removeItem('lastVisitedPath');
        window.sessionStorage.removeItem('user-register-data');
    } catch {
        /* Cookie and React cleanup must still complete when browser storage is blocked. */
    }
};

const resolveCanonicalTtHost = (host) => {
    const normalizedHost = (host || '').toLowerCase();
    if (!normalizedHost) {
        return TT_PROD_HOST;
    }

    if (
        normalizedHost.includes('tt-stg.') ||
        normalizedHost.includes('-stg.') ||
        normalizedHost.includes('.stg.') ||
        normalizedHost.includes('staging.') ||
        normalizedHost.includes('demo')
    ) {
        return TT_STG_HOST;
    }

    if (
        normalizedHost.includes('tt-dev.') ||
        normalizedHost.includes('-dev.') ||
        normalizedHost.includes('.dev.') ||
        normalizedHost === 'localhost' ||
        normalizedHost === '127.0.0.1' ||
        normalizedHost === '[::1]'
    ) {
        return TT_DEV_HOST;
    }

    return TT_PROD_HOST;
};

const buildCanonicalLoginUrl = (host) => {
    return `https://${host}${TT_LOGIN_PATH}`;
};

const resolveForcedLogoutLoginUrl = (loginUrl) => {
    if (typeof window === 'undefined') {
        return FORCED_LOGOUT_LOGIN_URL;
    }

    const fallbackHost = resolveCanonicalTtHost(window.location.hostname);
    const fallbackLoginUrl = buildCanonicalLoginUrl(fallbackHost);

    if (typeof loginUrl !== 'string' || !loginUrl.trim()) {
        return fallbackLoginUrl;
    }

    try {
        const parsedUrl = new URL(loginUrl, window.location.origin);
        const host = (parsedUrl.hostname || '').toLowerCase();
        if (CANONICAL_TT_HOSTS.has(host)) {
            // Keep users in the same environment family even when config is mis-set.
            if (resolveCanonicalTtHost(host) === fallbackHost) {
                return buildCanonicalLoginUrl(host);
            }
        }
    } catch {
        // fallback to canonical login URL
    }

    return fallbackLoginUrl;
};

const redirectToForcedLogoutLogin = (loginUrl, intentional = false) => {
    if (typeof window === 'undefined') {
        return;
    }

    const resolvedLoginUrl = resolveForcedLogoutLoginUrl(loginUrl);
    const redirectUrl = new URL(resolvedLoginUrl);
    if (!intentional) redirectUrl.searchParams.set(FORCED_LOGOUT_QUERY_PARAM, '1');

    let isAlreadyOnLogin = false;

    try {
        const targetUrl = new URL(redirectUrl.toString());
        const currentUrl = new URL(window.location.href);

        isAlreadyOnLogin =
            currentUrl.origin === targetUrl.origin &&
            currentUrl.pathname === targetUrl.pathname &&
            (intentional || currentUrl.searchParams.get(FORCED_LOGOUT_QUERY_PARAM) === '1');
    } catch {
        isAlreadyOnLogin = false;
    }

    if (!isAlreadyOnLogin) {
        window.location.href = redirectUrl.toString();
    }
};

export const handleForcedLogout = ({
    broadcast = true,
    onCleanup,
    loginUrl = '/auth/login',
    reason = 'forced'
} = {}) => {
    if (typeof window === 'undefined' || isForcedLogoutInProgress) {
        return;
    }

    isForcedLogoutInProgress = true;

    clearAuthStorage({ onCleanup });
    window.dispatchEvent(new Event(FORCED_LOGOUT_EVENT));
    try {
        if (reason !== 'signed-out')
            window.localStorage.setItem(FORCED_LOGOUT_NOTICE_KEY, Date.now().toString());
        else window.localStorage.removeItem(FORCED_LOGOUT_NOTICE_KEY);

        if (broadcast) {
            window.localStorage.setItem(
                FORCED_LOGOUT_STORAGE_KEY,
                reason === 'signed-out' ? 'signed-out' : Date.now().toString()
            );
        }
    } catch {
        /* The canonical login redirect must not depend on localStorage. */
    }

    redirectToForcedLogoutLogin(loginUrl, reason === 'signed-out');
};

export const attachForcedLogoutInterceptor = (client, options = {}) => {
    const getOnTokenUpgrade = () => {
        const onTokenUpgrade = options?.onTokenUpgrade;
        return typeof onTokenUpgrade === 'function' ? onTokenUpgrade : null;
    };

    const setTokenHeader = (config, token) => {
        if (!config || typeof config !== 'object') {
            return;
        }

        const headers = config.headers;
        if (headers && typeof headers === 'object' && typeof headers.set === 'function') {
            headers.set('token', token);
            config.headers = headers;
            return;
        }

        config.headers = { ...(headers || {}), token };
    };

    client.interceptors.response.use(
        async (response) => {
            const code = getCodeFromData(response?.data);
            if (code !== TOKEN_UPGRADED_CODE) {
                return response;
            }

            const upgradedToken = response?.data?.token;
            const originalConfig = response?.config;
            const onTokenUpgrade = getOnTokenUpgrade();

            if (
                typeof upgradedToken !== 'string' ||
                !upgradedToken.trim() ||
                !originalConfig ||
                originalConfig.__ttTokenUpgradedRetried === true ||
                typeof onTokenUpgrade !== 'function' ||
                typeof client !== 'function'
            ) {
                return response;
            }

            try {
                await onTokenUpgrade(upgradedToken);
            } catch {
                return response;
            }

            const retryConfig = { ...originalConfig, __ttTokenUpgradedRetried: true };
            setTokenHeader(retryConfig, upgradedToken);

            return client(retryConfig);
        },
        (error) => {
            if (isForcedLogoutError(error)) {
                handleForcedLogout({
                    ...options,
                    reason: isSessionRevokedError(error) ? 'signed-out' : 'forced'
                });
            }

            return Promise.reject(error);
        }
    );
};
